import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import { getUserDisplayLabel } from '../../utils/labels'

export default function AssignToSubgroup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const memberId = searchParams.get('memberId')
  
  const [users, setUsers] = useState([])
  const [subgroups, setSubgroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState(memberId || '')
  const [selectedSubgroup, setSelectedSubgroup] = useState('')

  useEffect(() => {
    loadUsers()
    loadSubgroups()
  }, [])

  const loadUsers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('org_id', userData.org_id)
        .in('role', ['member', 'unassigned'])
        .order('name')

      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Load users error:', error)
    }
  }

  const loadSubgroups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data: subgroupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 3)
        .order('name')

      const { data: groupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 2)

      const { data: teamsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)

      const subgroupsWithHierarchy = subgroupsData.map(subgroup => {
        const group = groupsData.find(d => d.id === subgroup.parent_id)
        const team = group ? teamsData.find(f => f.id === group.parent_id) : null
        return {
          ...subgroup,
          groupName: group ? group.name : 'Unknown',
          teamName: team ? team.name : 'Unknown'
        }
      })

      setSubgroups(subgroupsWithHierarchy || [])
    } catch (error) {
      console.error('Load subgroups error:', error)
    }
  }

  const handleAssign = async () => {
    if (!selectedUser || !selectedSubgroup) return

    setLoading(true)
    try {
      const user = users.find(u => u.id === selectedUser)

      const updates = {}

      // If user doesn't have QR code, generate one
      if (!user.qr_signature) {
        updates.qr_signature = user.id + '-' + Date.now()
        updates.qr_generated_at = new Date().toISOString()
      }

      // If user is unassigned, change to member (but keep leadership_level as null)
      if (user.role === 'unassigned') {
        updates.role = 'member'
        updates.leadership_level = null
      }

      // Update user if needed
      if (Object.keys(updates).length > 0) {
        await supabase
          .from('users')
          .update(updates)
          .eq('id', user.id)
      }

      // Check if already in this subgroup
      const { data: existing } = await supabase
        .from('group_members')
        .select('*')
        .eq('user_id', selectedUser)
        .eq('group_id', selectedSubgroup)
        .single()

      if (!existing) {
        await supabase
          .from('group_members')
          .insert({
            user_id: selectedUser,
            group_id: selectedSubgroup,
            is_leader: false
          })
      }

      alert('Member assigned to subgroup successfully!')
      navigate('/admin/members')
    } catch (error) {
      console.error('Assign error:', error)
      alert('Failed to assign member')
    } finally {
      setLoading(false)
    }
  }

  const selectedUserData = users.find(u => u.id === selectedUser)

  return (
    <DashboardLayout>
      <PageHeader title="Assign Member to Subgroup" description="Add a member to a subgroup">
        <Link
          to="/admin/members"
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </Link>
      </PageHeader>

      <div className="p-6">
        <div className="max-w-3xl">
          <Card>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Select Member *</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Choose member...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email}) - {getUserDisplayLabel(user)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Assign to Subgroup *</label>
                <select
                  value={selectedSubgroup}
                  onChange={(e) => setSelectedSubgroup(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Choose subgroup...</option>
                  {subgroups.map(subgroup => (
                    <option key={subgroup.id} value={subgroup.id}>
                      {subgroup.name} - {subgroup.groupName} ({subgroup.teamName})
                    </option>
                  ))}
                </select>
              </div>

              {selectedUserData && (
                <div className="bg-blue-50 p-4 rounded">
                  <p className="text-sm font-medium text-blue-900 mb-2">What will happen:</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Member will be added to selected subgroup</li>
                    {selectedUserData.role === 'unassigned' && (
                      <li>• Status changed to <strong>Member</strong></li>
                    )}
                    {!selectedUserData.qr_signature && (
                      <li>• QR code will be generated</li>
                    )}
                  </ul>
                </div>
              )}

              <button
                onClick={handleAssign}
                disabled={loading || !selectedUser || !selectedSubgroup}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Assign to Subgroup'}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}